import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient, formatApiError } from "@/lib/api";
import { formatGNF } from "@/utils/format";
import { toast } from "sonner";
import { Loader2, XCircle, ChefHat, Bike, PackageCheck, Clock, Store, Flame, Power, BellRing } from "lucide-react";
import CancelOrderModal from "@/components/CancelOrderModal";
import { playAlertSound, vibrate, unlockAudio } from "@/lib/pushNotifications";

const ETA_OPTIONS = [10, 20, 30, 45];
const COLS = [
  { k: "new", label: "Nouvelles", color: "#E32636" },
  { k: "prep", label: "__PREP__", color: "#F4C430" },
  { k: "ready", label: "__READY__", color: "#009E60" },
];

const isToday = (iso) => (iso || "").slice(0, 10) === new Date().toISOString().slice(0, 10);
const shortId = (o) => o.order_id.slice(-6).toUpperCase();

/** Kitchen-tablet board: today's orders in 3 columns (swipeable tabs on mobile).
 * NOUVELLES → combined "Accepter · X min" | EN PRÉPARATION → big "Prête" + countdown
 * | PRÊTES / EN LIVRAISON. Fixed top bar: OUVERT / OCCUPÉ / FERMÉ one-tap status. */
export default function KitchenBoard() {
  const { t } = useTranslation();
  const colLabel = (c) => c.label === "__PREP__" ? t("rdash.kbPreparing") : c.label === "__READY__" ? t("rdash.kbReadyDelivery") : t("rdash.kbNew");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [cancelId, setCancelId] = useState(null);
  const [mobileCol, setMobileCol] = useState("new");
  const [, setTick] = useState(0); // re-render for countdowns

  const refresh = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/my/restaurant/orders");
      setOrders((data || []).filter((o) => isToday(o.created_at)));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const p = setInterval(refresh, 12000);
    const c = setInterval(() => setTick((x) => x + 1), 30000);
    return () => { clearInterval(p); clearInterval(c); };
  }, [refresh]);

  // Son + vibration RÉPÉTÉS quand une NOUVELLE commande arrive, jusqu'à
  // consultation (bandeau "Consulter") ou acceptation/annulation de la commande.
  const seenRef = useRef(new Set());
  const firstRef = useRef(true);
  const alarmRef = useRef(new Set()); // commandes non consultées déclenchant l'alarme
  const alarmTimer = useRef(null);
  const [alarmCount, setAlarmCount] = useState(0);

  const stopAlarm = useCallback(() => {
    alarmRef.current.clear();
    setAlarmCount(0);
  }, []);

  useEffect(() => {
    const received = new Set(orders.filter((o) => o.status === "received").map((o) => o.order_id));
    if (firstRef.current) {
      if (!loading) firstRef.current = false;
      received.forEach((id) => seenRef.current.add(id));
      return;
    }
    // Une commande acceptée/annulée est considérée consultée → sortir de l'alarme
    for (const id of [...alarmRef.current]) {
      if (!received.has(id)) alarmRef.current.delete(id);
    }
    const fresh = [...received].filter((id) => !seenRef.current.has(id));
    if (fresh.length > 0) {
      fresh.forEach((id) => alarmRef.current.add(id));
      playAlertSound("priority");
      vibrate([300, 100, 300]);
      toast.warning(fresh.length > 1 ? `${fresh.length} nouvelles commandes !` : t("rdash.kbNewOrder"), { duration: 8000 });
      setMobileCol("new");
    }
    setAlarmCount(alarmRef.current.size);
    received.forEach((id) => seenRef.current.add(id));
  }, [orders, loading]);

  // Boucle d'alarme : rejoue son + vibration toutes les 6 s tant que non consultée
  useEffect(() => {
    if (alarmCount > 0 && !alarmTimer.current) {
      alarmTimer.current = setInterval(() => {
        playAlertSound("priority");
        vibrate([300, 100, 300]);
      }, 6000);
    } else if (alarmCount === 0 && alarmTimer.current) {
      clearInterval(alarmTimer.current);
      alarmTimer.current = null;
    }
    return () => {
      if (alarmTimer.current) { clearInterval(alarmTimer.current); alarmTimer.current = null; }
    };
  }, [alarmCount]);

  const setStatus = async (o, status, etaMinutes = null, msg = "Commande mise à jour") => {
    setBusy((b) => ({ ...b, [o.order_id]: true }));
    try {
      const body = { status };
      if (etaMinutes) body.eta_minutes = etaMinutes;
      await apiClient.patch(`/orders/${o.order_id}/status`, body);
      toast.success(msg);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, [o.order_id]: false }));
    }
  };

  const groups = {
    new: orders.filter((o) => o.status === "received"),
    prep: orders.filter((o) => o.status === "preparing"),
    ready: orders.filter((o) => ["ready", "delivering"].includes(o.status)),
  };

  return (
    <div data-testid="kitchen-board">
      {/* Fixed one-tap service status bar */}
      <ServiceStateBar />

      {/* Bandeau d'alarme — nouvelle(s) commande(s) non consultée(s) */}
      {alarmCount > 0 && (
        <button
          onClick={() => { stopAlarm(); setMobileCol("new"); }}
          data-testid="new-order-alarm-banner"
          className="w-full mt-4 rounded-2xl bg-brand-red text-white px-4 py-3.5 font-semibold flex items-center justify-between gap-3 animate-pulse shadow-lg"
        >
          <span className="flex items-center gap-2 min-w-0">
            <BellRing className="w-5 h-5 shrink-0" />
            <span className="truncate">{alarmCount > 1 ? `${alarmCount} nouvelles commandes !` : t("rdash.kbNewOrder")}</span>
          </span>
          <span className="text-xs bg-white/20 rounded-full px-3 py-1.5 shrink-0">{t("rdash.kbCheckStopAlert")}</span>
        </button>
      )}

      {/* Mobile column tabs */}
      <div className="flex md:hidden gap-2 my-4">
        {COLS.map((c) => (
          <button
            key={c.k}
            onClick={() => { unlockAudio(); setMobileCol(c.k); }}
            data-testid={`kb-tab-${c.k}`}
            className={`flex-1 rounded-full py-2.5 text-xs font-bold border transition-colors ${mobileCol === c.k ? "text-white" : "bg-white text-ink border-sand"}`}
            style={mobileCol === c.k ? { backgroundColor: c.color, borderColor: c.color } : {}}
          >
            {colLabel(c)} ({groups[c.k].length})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-mute py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("rdash.kbLoading")}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:mt-4">
          {COLS.map((c) => (
            <div key={c.k} className={`${mobileCol === c.k ? "" : "hidden"} md:block`} data-testid={`kb-col-${c.k}`}>
              <div className="hidden md:flex items-center gap-2 mb-3">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-mute">{colLabel(c)}</span>
                <span className="text-xs font-bold rounded-full px-2 py-0.5" style={{ backgroundColor: c.color + "22", color: c.color }}>{groups[c.k].length}</span>
              </div>
              <div className="space-y-3">
                {groups[c.k].length === 0 && (
                  <div className="bg-white/60 border border-dashed border-sand rounded-2xl p-6 text-center text-mute text-xs">
                    {c.k === "new" ? t("rdash.kbNothingNew", "Aucune nouvelle commande") : c.k === "prep" ? t("rdash.kbNothingPreparing") : t("rdash.kbNothingDeliver")}
                  </div>
                )}
                {groups[c.k].map((o) =>
                  c.k === "new" ? (
                    <NewCard key={o.order_id} o={o} busy={busy[o.order_id]} onAccept={setStatus} onCancel={setCancelId} />
                  ) : c.k === "prep" ? (
                    <PrepCard key={o.order_id} o={o} busy={busy[o.order_id]} onReady={setStatus} onCancel={setCancelId} />
                  ) : (
                    <ReadyCard key={o.order_id} o={o} />
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <CancelOrderModal open={!!cancelId} onClose={() => setCancelId(null)} orderId={cancelId} onCancelled={() => { setCancelId(null); refresh(); }} />
    </div>
  );
}

function CardShell({ o, children, highlight }) {
  return (
    <div
      data-testid={`kb-order-${o.order_id}`}
      className={`bg-white rounded-2xl p-4 border transition-shadow ${highlight ? "border-brand-red ring-2 ring-brand-red/30 shadow-lg animate-pulse-slow" : "border-sand"}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="font-display text-lg font-bold">#{shortId(o)}</div>
          <div className="text-[11px] text-mute">{new Date(o.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} · {o.zone}</div>
          {o.is_gift && <div className="mt-1 inline-flex rounded-full bg-brand-yellow/25 px-2 py-0.5 text-[10px] font-bold">🎁 Cadeau pour {o.gift_recipient_name}</div>}
        </div>
        <div className="font-display text-lg font-semibold shrink-0" data-testid={`kb-order-amount-${o.order_id}`}>{formatGNF(o.subtotal)}</div>
      </div>
      <div className="text-sm border-t border-sand pt-2 mb-3 leading-relaxed">
        {o.items?.map((it, idx) => (
          <div key={(it.dish_id || it.name) + idx} className="mb-1">
            <div className="flex justify-between gap-2">
              <span><b>{it.quantity}×</b> {it.name}</span>
            </div>
            {it.options?.length > 0 && (
              <div className="text-xs text-[#8A6D00] font-medium pl-4" data-testid={`kb-options-${o.order_id}-${idx}`}>
                {it.options.map((op) => op.name).join(" · ")}
              </div>
            )}
            {it.note && (
              <div className="text-xs text-brand-red italic pl-4" data-testid={`kb-note-${o.order_id}-${idx}`}>« {it.note} »</div>
            )}
          </div>
        ))}
        {o.notes && (
          <div className="mt-1.5 rounded-lg bg-[#FFF8E6] border border-brand-yellow/40 px-2.5 py-1.5 text-xs italic" data-testid={`kb-order-note-${o.order_id}`}>
            💬 Message du client : « {o.notes} »
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

function SmallActions({ o, onCancel }) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2 mt-2">
      <button onClick={() => onCancel(o.order_id)} data-testid={`kb-cancel-${o.order_id}`} className="flex-1 rounded-xl bg-brand-red/5 text-brand-red hover:bg-brand-red/15 py-2 text-xs font-semibold inline-flex items-center justify-center gap-1">
        <XCircle className="w-3.5 h-3.5" /> {t("rdash.kbCancel")}
      </button>
    </div>
  );
}

/** NOUVELLE : combined accept + ETA in one tap */
function NewCard({ o, busy, onAccept, onCancel }) {
  const { t } = useTranslation();
  return (
    <CardShell o={o} highlight>
      <div className="text-[11px] uppercase tracking-widest font-bold text-brand-red mb-1.5">{t("rdash.kbAcceptPrepTime")}</div>
      <div className="grid grid-cols-4 gap-1.5">
        {ETA_OPTIONS.map((m) => (
          <button
            key={m}
            disabled={busy}
            onClick={() => { unlockAudio(); onAccept(o, "preparing", m, `Acceptée — prête dans ~${m} min`); }}
            data-testid={`kb-accept-${m}-${o.order_id}`}
            className="rounded-xl bg-brand-green hover:bg-[#007A4A] disabled:opacity-60 text-white py-3 text-sm font-bold"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : `${m}′`}
          </button>
        ))}
      </div>
      <SmallActions o={o} onCancel={onCancel} />
    </CardShell>
  );
}

/** EN PRÉPARATION : big "Prête pour livraison" + countdown of announced time */
function PrepCard({ o, busy, onReady, onCancel }) {
  const { t } = useTranslation();
  const left = o.ready_estimate_at ? Math.round((new Date(o.ready_estimate_at).getTime() - Date.now()) / 60000) : null;
  return (
    <CardShell o={o}>
      {left != null && (
        <div className={`mb-2 rounded-xl px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 ${left >= 0 ? "bg-brand-yellow/20 text-[#8A6D00]" : "bg-brand-red/10 text-brand-red"}`} data-testid={`kb-countdown-${o.order_id}`}>
          <Clock className="w-3.5 h-3.5" />
          {left >= 0 ? `${left} min restantes (annoncé)` : `En retard de ${Math.abs(left)} min`}
        </div>
      )}
      <button
        disabled={busy}
        onClick={() => onReady(o, "ready", null, t("rdash.kbReadySearching"))}
        data-testid={`kb-ready-${o.order_id}`}
        className="w-full rounded-xl bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white py-4 text-base font-bold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <PackageCheck className="w-5 h-5" />}
        {t("rdash.kbReadyForDelivery")}
      </button>
      <SmallActions o={o} onCancel={onCancel} />
    </CardShell>
  );
}

/** PRÊTE / EN LIVRAISON */
function ReadyCard({ o }) {
  const { t } = useTranslation();
  return (
    <CardShell o={o}>
      {o.status === "ready" ? (
        <div className="rounded-xl bg-elevated px-3 py-2.5 text-xs font-semibold text-mute flex items-center gap-2" data-testid={`kb-dispatch-${o.order_id}`}>
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-red opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-red" />
          </span>
          {t("rdash.kbSearchingDriver")}
        </div>
      ) : (
        <div className="rounded-xl bg-[#E7F6EE] px-3 py-2.5 text-xs font-semibold text-brand-green flex items-center gap-2" data-testid={`kb-driver-${o.order_id}`}>
          <Bike className="w-4 h-4" /> {o.driver_name ? `${t("rdash.kbDelivering")} — ${o.driver_name}` : t("rdash.kbDelivering")}
        </div>
      )}
    </CardShell>
  );
}

/** One-tap OUVERT / OCCUPÉ / FERMÉ bar (sticky under the navbar) */
function ServiceStateBar() {
  const { t } = useTranslation();
  const [state, setState] = useState(null); // open | busy | closed
  const [scheduleOn, setScheduleOn] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/my/restaurant");
      if (!data) return;
      setScheduleOn(!!data.hours?.enabled);
      setState(!data.open ? "closed" : data.busy ? "busy" : "open");
    } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setSvc = async (s) => {
    if (s === state || saving) return;
    unlockAudio();
    setSaving(true);
    try {
      await apiClient.patch(`/my/restaurant/service-state?state=${s}`);
      setState(s);
      toast.success(s === "open" ? t("rdash.kbOpenToast") : s === "busy" ? t("rdash.kbBusyToast") : t("rdash.kbClosedToast"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const OPTS = [
    { k: "open", label: t("rdash.kbOpen"), icon: <Store className="w-4 h-4" />, cls: "bg-brand-green text-white" },
    { k: "busy", label: t("rdash.kbBusy"), icon: <Flame className="w-4 h-4" />, cls: "bg-[#E67E22] text-white" },
    { k: "closed", label: t("rdash.kbClosed"), icon: <Power className="w-4 h-4" />, cls: "bg-brand-red text-white" },
  ];

  return (
    <div className="sticky top-16 z-30 -mx-4 px-4 py-2.5 bg-sand/95 backdrop-blur border-b border-sand" data-testid="service-state-bar">
      <div className="flex items-center gap-2">
        <div className="flex flex-1 gap-1.5 bg-white rounded-full p-1 border border-sand max-w-md">
          {OPTS.map((op) => (
            <button
              key={op.k}
              onClick={() => setSvc(op.k)}
              disabled={saving}
              data-testid={`service-state-${op.k}`}
              className={`flex-1 rounded-full py-2.5 text-xs font-extrabold tracking-wide inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60 ${state === op.k ? op.cls : "text-mute hover:bg-elevated"}`}
            >
              {op.icon} {op.label}
            </button>
          ))}
        </div>
        <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-mute">
          <ChefHat className="w-3.5 h-3.5" />
          {state === "busy" ? t("rdash.kbBusyHint") : scheduleOn ? t("rdash.kbScheduleOn") : t("rdash.kbTapStatus")}
        </div>
      </div>
    </div>
  );
}
